import { useEffect, useMemo, useRef, useState } from 'react';
import type { StructuralPieceId } from '@obraplan/core/domain/shared/ids';
import type { Point } from '@obraplan/core/domain/takeoff/polygon';
import {
  checkStairFields,
  flightRise,
  flightWidth,
  type Stair,
  type StairFlight,
} from '@obraplan/core/domain/takeoff/stair';
import {
  contornoDoAmbienteEscada,
  DEGRAUS_PADRAO,
  degrausSugeridos,
  emMetros,
  MEDIDAS_PADRAO_DA_ESCADA_EM_CM,
  proximoNomeDeEscada,
} from './escada-da-prancha';
import { retanguloPorDoisCantos } from './fundacao-da-prancha';
import { distance, metresToPixel, pixelToMetres, resolvePoint, type Pixel } from './plan-tracer';
import type { TypologyDraft, WorkspaceAction } from './typology-draft';

/**
 * O que as ferramentas "Escada", "Lance" e "Patamar" fazem na prancha (Fase 27, etapa 5 — D10,
 * F17, RN-373).
 *
 * - **Escada**: o primeiro lance, por retângulo de dois cantos — arrastando ou clicando. O
 *   sentido da subida é do primeiro canto ao segundo. O painel pede os degraus, o espelho, o
 *   piso e a espessura, e mostra a largura que o contorno deu e a altura que o lance vence;
 * - **Lance** e **Patamar**: com a escada selecionada, o mesmo traço acrescenta um lance ou um
 *   patamar a ela. O domínio recusa o contorno sem área e a medida sem número, e o painel diz
 *   isso na hora, antes de gravar.
 *
 * Com a prancha parada, clicar na escada a seleciona inteira; clicar numa parte dela, estando a
 * escada já selecionada, seleciona a parte. Arrastar leva a escada inteira, lances e patamares
 * juntos. O estado mora aqui, e não dentro do traçado, pelo mesmo motivo do hook da laje.
 */

export type FerramentaDaEscada = 'ESCADA' | 'LANCE' | 'PATAMAR';

/** O que se pode selecionar na prancha, entre as peças desta fatia. */
export type PecaDaEscada =
  | { readonly tipo: 'ESCADA'; readonly id: StructuralPieceId }
  | { readonly tipo: 'LANCE'; readonly id: StructuralPieceId; readonly indice: number }
  | { readonly tipo: 'PATAMAR'; readonly id: StructuralPieceId; readonly indice: number };

/** A parte de uma escada clicada, quando o clique foi numa delas. */
export type ParteDaEscada =
  | { readonly tipo: 'LANCE'; readonly indice: number }
  | { readonly tipo: 'PATAMAR'; readonly indice: number };

/** A escada na mão, arrastada: o pixel por onde ela passa até o botão subir. */
export interface ArrastoDaEscada {
  readonly id: StructuralPieceId;
  readonly origem: Pixel;
  readonly atual: Pixel;
  /** Passou da folga do clique? Antes disso é clique, e clicar seleciona. */
  readonly moveu: boolean;
}

/** O retângulo ainda não cadastrado, como a prancha o desenha. */
export interface TracoDaEscada {
  readonly vertices: readonly Pixel[];
  /** Os cantos clicados — as bolinhas. */
  readonly marcados: readonly Pixel[];
}

const FOLGA_DO_ARRASTO = 4;
const FOLGA_VERTICE = 10;

/** Quanto tempo depois de soltar um arrasto o clique que veio com ele ainda é dele. */
const JANELA_DO_CLIQUE_MS = 100;

/** Para conferir só o nome e as medidas enquanto o contorno ainda não existe. */
const QUADRADO: readonly Point[] = [
  { x: 0, y: 0 },
  { x: 1, y: 0 },
  { x: 1, y: 1 },
  { x: 0, y: 1 },
];

const SEM_ALVO = 'Selecione a escada antes de desenhar o lance ou o patamar.';

function escadaDoAlvo(alvo: Element): StructuralPieceId | undefined {
  const id = alvo.closest('[data-escada]')?.getAttribute('data-escada');
  return id === null || id === undefined ? undefined : (id as StructuralPieceId);
}

/** Qual parte da escada o clique pegou: o lance ou o patamar mais perto do alvo. */
export function parteDoAlvo(alvo: Element): ParteDaEscada | undefined {
  const lance = alvo.closest('[data-lance]')?.getAttribute('data-lance');
  if (lance !== null && lance !== undefined) return { tipo: 'LANCE', indice: Number(lance) };
  const patamar = alvo.closest('[data-patamar]')?.getAttribute('data-patamar');
  if (patamar !== null && patamar !== undefined) {
    return { tipo: 'PATAMAR', indice: Number(patamar) };
  }
  return undefined;
}

export function useEscadaNaPrancha({
  draft,
  escala,
  zoom,
  ferramenta,
  parado,
  selecionada,
  dispatch,
  selecionar,
}: {
  draft: TypologyDraft;
  escala: number | undefined;
  zoom: number;
  /** A ferramenta ligada, se for uma destas. Parada, a prancha só seleciona e arrasta. */
  ferramenta: FerramentaDaEscada | undefined;
  /** A prancha está parada — sem ferramenta nenhuma ligada? */
  parado: boolean;
  /** O que está selecionado na prancha, se for uma peça desta fatia: o lance nasce nela. */
  selecionada: PecaDaEscada | undefined;
  dispatch: React.Dispatch<WorkspaceAction>;
  selecionar: (peca: PecaDaEscada) => void;
}) {
  const escadas = useMemo(
    () => (draft.stairs ?? []).filter((escada) => escada.deletedAt === undefined),
    [draft.stairs],
  );

  /** A escada que o lance ou o patamar vai receber: a selecionada. */
  const alvo = useMemo(
    (): Stair | undefined =>
      selecionada === undefined
        ? undefined
        : escadas.find((escada) => escada.id === selecionada.id),
    [escadas, selecionada],
  );

  /** O contorno do ambiente ESCADA da área comum, se está traçado — F17. */
  const contornoDoAmbiente = useMemo(() => contornoDoAmbienteEscada(draft.rooms), [draft.rooms]);

  /** Os dois cantos do retângulo, em pixel da prancha. */
  const [pontos, setPontos] = useState<readonly Pixel[]>([]);
  /** O retângulo arrastado, antes de o botão subir. */
  const [gesto, setGesto] = useState<
    { readonly origem: Pixel; readonly atual: Pixel; readonly moveu: boolean } | undefined
  >(undefined);
  const [nomeDigitado, setNomeDigitado] = useState<string | undefined>(undefined);
  /** Ausente, os degraus saem do comprimento do lance dividido pelo piso. */
  const [degrausDigitados, setDegrausDigitados] = useState<number | undefined>(undefined);
  /** Centímetros, como a obra fala: 17,5 de espelho, 28 de piso, 12 de laje. */
  const [espelhoEmCm, setEspelhoEmCm] = useState<number>(MEDIDAS_PADRAO_DA_ESCADA_EM_CM.espelho);
  const [pisoEmCm, setPisoEmCm] = useState<number>(MEDIDAS_PADRAO_DA_ESCADA_EM_CM.piso);
  const [espessuraEmCm, setEspessuraEmCm] = useState<number>(
    MEDIDAS_PADRAO_DA_ESCADA_EM_CM.espessura,
  );
  const [espessuraDoPatamarEmCm, setEspessuraDoPatamarEmCm] = useState<number>(
    MEDIDAS_PADRAO_DA_ESCADA_EM_CM.espessura,
  );

  const [cursor, setCursor] = useState<Pixel | undefined>(undefined);
  const [arrastando, setArrastando] = useState<ArrastoDaEscada | undefined>(undefined);
  /** Quando o último arrasto terminou — o clique que vem junto com ele não é de ninguém. */
  const cliqueDoArrasto = useRef<number | undefined>(undefined);

  // Trocar de tipologia ou de ferramenta descarta o que estava na mão: o ponto marcado é do
  // desenho e da ferramenta que saíram da mesa.
  const emDesenho = draft.unitTypeId;
  useEffect(() => {
    setPontos([]);
    setGesto(undefined);
    setCursor(undefined);
    setArrastando(undefined);
    setNomeDigitado(undefined);
    setDegrausDigitados(undefined);
  }, [emDesenho, ferramenta]);

  const nome = nomeDigitado ?? proximoNomeDeEscada(escadas);

  /** Os vértices a que o canto novo se encaixa: os das escadas já desenhadas. */
  const verticesParaEncaixe = useMemo((): readonly Pixel[] => {
    if (escala === undefined) return [];
    const contornos = escadas.flatMap((escada) => [
      ...escada.flights.map((lance) => lance.outline),
      ...escada.landings.map((patamar) => patamar.outline),
    ]);
    return contornos.flatMap((contorno) => contorno.map((ponto) => metresToPixel(ponto, escala)));
  }, [escala, escadas]);

  /** O contorno de agora, em metros — ausente enquanto os dois cantos não estão marcados. */
  const contorno = useMemo((): readonly Point[] | undefined => {
    if (escala === undefined) return undefined;
    const [a, b] = pontos;
    return a === undefined || b === undefined
      ? undefined
      : retanguloPorDoisCantos(pixelToMetres(a, escala), pixelToMetres(b, escala));
  }, [escala, pontos]);

  const degraus =
    degrausDigitados ??
    (contorno === undefined ? DEGRAUS_PADRAO : degrausSugeridos(contorno, emMetros(pisoEmCm)));

  /** O lance de agora, com o contorno que há — ou o quadrado de conferência, sem contorno. */
  const lance = useMemo(
    (): StairFlight => ({
      outline: contorno ?? QUADRADO,
      steps: degraus,
      riser: emMetros(espelhoEmCm),
      tread: emMetros(pisoEmCm),
      thickness: emMetros(espessuraEmCm),
    }),
    [contorno, degraus, espelhoEmCm, pisoEmCm, espessuraEmCm],
  );

  /** A largura que o contorno deu e a altura que os degraus vencem — ausentes sem contorno. */
  const largura = contorno === undefined ? undefined : flightWidth(lance);
  const altura = contorno === undefined ? undefined : flightRise(lance);

  /**
   * O que impede cadastrar o traço de agora — o painel diz, e o botão espera.
   *
   * É a mesma conferência do domínio, antes de gravar: o nome repetido, o degrau sem medida e a
   * espessura zerada aparecem aqui, enquanto o traço ainda pode ser refeito.
   */
  const impedimento = useMemo((): string | undefined => {
    if (ferramenta === 'LANCE' || ferramenta === 'PATAMAR') {
      if (alvo === undefined) return SEM_ALVO;
    }
    const conferida =
      ferramenta === 'PATAMAR' && alvo !== undefined
        ? checkStairFields(
            {
              name: alvo.name,
              flights: alvo.flights,
              landings: [
                ...alvo.landings,
                { outline: contorno ?? QUADRADO, thickness: emMetros(espessuraDoPatamarEmCm) },
              ],
            },
            escadas,
            alvo.id,
          )
        : ferramenta === 'LANCE' && alvo !== undefined
          ? checkStairFields(
              { name: alvo.name, flights: [...alvo.flights, lance], landings: alvo.landings },
              escadas,
              alvo.id,
            )
          : checkStairFields({ name: nome, flights: [lance], landings: [] }, escadas);
    return conferida.ok ? undefined : conferida.error.message;
  }, [ferramenta, alvo, contorno, lance, nome, escadas, espessuraDoPatamarEmCm]);

  /** Há traço pela metade? Aí o clique é do traço, e a escada que está embaixo deixa passar. */
  const emTraco = pontos.length > 0 || gesto !== undefined;

  /** A escada responde a clique com a prancha parada, ou com a ferramenta dela sem traço. */
  const clicavel = parado || (ferramenta === 'ESCADA' && !emTraco);

  /** Onde o canto cai: encaixado no vértice de uma escada, ou onde foi clicado. */
  function cantoNovo(bruto: Pixel): Pixel {
    return resolvePoint(bruto, {
      vertices: [...verticesParaEncaixe, ...pontos],
      vertexTolerance: FOLGA_VERTICE / zoom,
    });
  }

  /** O clique no vazio da prancha, com uma das ferramentas ligadas. */
  function clicarNaPrancha(bruto: Pixel): void {
    if (escala === undefined || ferramenta === undefined) return;
    const canto = cantoNovo(bruto);
    const primeiro = pontos[0];
    if (pontos.length === 1 && primeiro !== undefined) {
      // O segundo clique no mesmo canto não é retângulo de tamanho zero.
      if (distance(primeiro, canto) < FOLGA_VERTICE / zoom) return;
      setPontos([primeiro, canto]);
      return;
    }
    // Zero ou dois cantos: o clique começa outro retângulo.
    setPontos([canto]);
  }

  /** O botão desceu na prancha: pode ser o começo de um arrasto de canto a canto. */
  function apertarNaPrancha(ponto: Pixel): void {
    if (ferramenta === undefined || escala === undefined) return;
    setGesto({ origem: ponto, atual: ponto, moveu: false });
  }

  /** O botão desceu numa escada: se andar, ela vai junto, lances e patamares. */
  function apertarNaEscada(alvoDoClique: Element, ponto: Pixel): boolean {
    if (escala === undefined || !clicavel) return false;
    const id = escadaDoAlvo(alvoDoClique);
    if (id === undefined || !escadas.some((escada) => escada.id === id)) return false;
    setArrastando({ id, origem: ponto, atual: ponto, moveu: false });
    return true;
  }

  /** O mouse andou: a escada na mão, o retângulo arrastado e o lado vivo. */
  function acompanhar(ponto: Pixel): boolean {
    if (arrastando !== undefined) {
      setArrastando({
        ...arrastando,
        atual: ponto,
        moveu: arrastando.moveu || distance(arrastando.origem, ponto) > FOLGA_DO_ARRASTO / zoom,
      });
      return true;
    }
    if (gesto !== undefined) {
      setGesto({
        ...gesto,
        atual: ponto,
        moveu: gesto.moveu || distance(gesto.origem, ponto) > FOLGA_DO_ARRASTO / zoom,
      });
      return true;
    }
    // O retângulo vivo só existe com um canto marcado; fora disso o cursor não custa render.
    if (ferramenta !== undefined && pontos.length === 1) setCursor(ponto);
    return false;
  }

  /** O botão subiu: grava o movimento, ou fecha o retângulo arrastado. */
  function soltar(ponto: Pixel): boolean {
    const corrente = arrastando;
    if (corrente !== undefined) {
      setArrastando(undefined);
      if (escala === undefined) return true;
      // Soltar onde apertou é clique, e clicar seleciona: a escada não sai do lugar.
      if (!corrente.moveu && distance(corrente.origem, ponto) <= FOLGA_DO_ARRASTO / zoom) {
        return true;
      }
      cliqueDoArrasto.current = Date.now();
      dispatch({
        type: 'UPDATE_STAIR',
        pieceId: corrente.id,
        shift: {
          x: (ponto.x - corrente.origem.x) * escala,
          y: (ponto.y - corrente.origem.y) * escala,
        },
      });
      return true;
    }

    const desenhado = gesto;
    if (desenhado !== undefined) {
      setGesto(undefined);
      // Sem andar, é clique: o clique que vem a seguir marca o canto.
      if (!desenhado.moveu && distance(desenhado.origem, ponto) <= FOLGA_DO_ARRASTO / zoom) {
        return false;
      }
      cliqueDoArrasto.current = Date.now();
      const a = cantoNovo(desenhado.origem);
      const b = cantoNovo(ponto);
      if (distance(a, b) >= FOLGA_VERTICE / zoom) setPontos([a, b]);
      return true;
    }
    return false;
  }

  /**
   * O clique que o fim do arrasto dispara é do arrasto; consome-o, e só uma vez. A janela
   * existe para que um arrasto cujo clique nunca veio — soltar fora da prancha — não engula o
   * clique seguinte, que é de outra pessoa.
   */
  function foiCliqueDoArrasto(): boolean {
    const desde = cliqueDoArrasto.current;
    cliqueDoArrasto.current = undefined;
    return desde !== undefined && Date.now() - desde < JANELA_DO_CLIQUE_MS;
  }

  function limparTraco(): void {
    setPontos([]);
    setGesto(undefined);
    setCursor(undefined);
  }

  function adicionar(): void {
    if (contorno === undefined || impedimento !== undefined || ferramenta === undefined) return;
    if (ferramenta === 'ESCADA') {
      dispatch({ type: 'ADD_STAIR', name: nome, flight: lance });
      setNomeDigitado(undefined);
      setDegrausDigitados(undefined);
    } else if (alvo !== undefined && ferramenta === 'LANCE') {
      dispatch({ type: 'ADD_STAIR_FLIGHT', pieceId: alvo.id, flight: lance });
      setDegrausDigitados(undefined);
    } else if (alvo !== undefined) {
      dispatch({
        type: 'ADD_STAIR_LANDING',
        pieceId: alvo.id,
        landing: { outline: contorno, thickness: emMetros(espessuraDoPatamarEmCm) },
      });
    }
    limparTraco();
  }

  /** O traço como o desenho o mostra: o retângulo, com o primeiro canto marcado. */
  const traco = ((): TracoDaEscada | undefined => {
    if (ferramenta === undefined) return undefined;
    const [a, b] = pontos;
    if (gesto?.moveu === true) {
      return {
        vertices: retanguloPorDoisCantos(gesto.origem, gesto.atual),
        marcados: [gesto.origem],
      };
    }
    if (a !== undefined && b !== undefined) {
      return { vertices: retanguloPorDoisCantos(a, b), marcados: [a, b] };
    }
    if (a !== undefined) {
      return {
        vertices: cursor === undefined ? [] : retanguloPorDoisCantos(a, cursor),
        marcados: [a],
      };
    }
    return undefined;
  })();

  /** O contorno do ambiente ESCADA vira o primeiro lance de partida: os dois cantos opostos. */
  function usarContornoDoAmbiente(): void {
    if (escala === undefined || contornoDoAmbiente === undefined) return;
    const a = contornoDoAmbiente[0];
    const b = contornoDoAmbiente[2];
    if (a === undefined || b === undefined) return;
    setPontos([metresToPixel(a, escala), metresToPixel(b, escala)]);
    setGesto(undefined);
    setCursor(undefined);
  }

  return {
    ferramenta,
    escadas,
    alvo,
    contornoDoAmbiente,
    cantos: pontos.length,
    contorno,
    traco,
    nome,
    digitarNome: setNomeDigitado,
    degraus,
    mudarDegraus: setDegrausDigitados,
    espelhoEmCm,
    mudarEspelhoEmCm: setEspelhoEmCm,
    pisoEmCm,
    mudarPisoEmCm: setPisoEmCm,
    espessuraEmCm,
    mudarEspessuraEmCm: setEspessuraEmCm,
    espessuraDoPatamarEmCm,
    mudarEspessuraDoPatamarEmCm: setEspessuraDoPatamarEmCm,
    largura,
    altura,
    lance,
    impedimento,
    adicionar,
    usarContornoDoAmbiente,
    desfazerPonto: () => setPontos((atual) => atual.slice(0, -1)),
    refazer: limparTraco,
    // O que os eventos do desenho chamam
    arrastando,
    clicavel,
    apertarNaPrancha,
    apertarNaEscada,
    acompanhar,
    soltar,
    cancelarArrasto: () => {
      setArrastando(undefined);
      setGesto(undefined);
    },
    foiCliqueDoArrasto,
    clicarNaPrancha,
    /**
     * O clique na escada: seleciona a escada inteira — e, estando ela (ou uma parte dela) já
     * selecionada, o clique numa parte seleciona a parte.
     */
    clicarNaEscada: (id: StructuralPieceId, parte: ParteDaEscada | undefined) => {
      if (foiCliqueDoArrasto()) return;
      if (parte !== undefined && selecionada?.id === id) selecionar({ ...parte, id });
      else selecionar({ tipo: 'ESCADA', id });
    },
  };
}

export type EscadaNaPrancha = ReturnType<typeof useEscadaNaPrancha>;
